import { getPhoneCallOutcomeLabel } from "../models/phoneCallOutcomes";
import { useEffect, useMemo, useState } from "react";
import DealDetailsModal from "../components/modals/deals/[id]";
import { getDeals } from "../services/contact_service";
import "../styles/deals.css";
import { DataTable, Page } from "@iq/models";
import { FiEye, FiPhone } from "react-icons/fi";
import AddPhoneCall from "../components/modals/AddPhoneCall";
import { getAcquisitionSourceLabel, getPrimaryOfferLabel } from "../models/opportunityModel";

function formatDashboardDateTime(value) {
    if (!value || value === "-" || value === "N/A") {
        return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleString();
}

function formatDashboardTouches(value) {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return String(value);
    }

    return String(parsed);
}

function formatDealValue(row) {
    const cents = Number(row?.amount_cents);
    if (Number.isFinite(cents) && cents > 0) {
        return `$${(cents / 100).toLocaleString()}`;
    }

    const rawValue = row?.deal_value ?? row?.value ?? row?.amount;
    if (rawValue === null || rawValue === undefined || rawValue === "") {
        return "-";
    }

    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
        return String(rawValue);
    }

    return `$${parsed.toLocaleString()}`;
}

function buildDealContact(row) {
    return {
        id: row?.contact_id ?? row?.contactId ?? row?.contact?.id ?? null,
        name: row?.contact_name ?? row?.manual_contact_name ?? row?.contact?.name ?? "",
        account: row?.account ?? row?.organization_name ?? row?.company ?? "",
        email: row?.email ?? row?.contact_email ?? row?.contact?.email ?? "",
        phone: row?.phone ?? row?.phone_number ?? row?.mobile ?? row?.contact_phone ?? row?.contact?.phone ?? "",
    };
}

const DEALS_TABLE_CONFIG = {
    columns: [
        { 
            key: "stage", 
            label: "Stage", 
            accessor: (row) => row?.deal_stage || row?.stage || row?.status || "-", 
            filterable: true, 
            quickValues: ["Won", "Lost"],
            width: ".75fr" 
        },
        { 
            key: "name", 
            label: "Name", 
            accessor: (row) => row?.contact_name || row?.manual_contact_name || row?.contact?.name || "-", 
            filterable: true, 
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr" 
        },
        { 
            key: "account", 
            label: "Account", 
            accessor: (row) => row?.account || row?.organization_name || row?.company || "-", 
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"]
        },
        { 
            key: "email", 
            label: "Email Address", 
            accessor: (row) => row?.email || row?.contact_email || row?.contact?.email || "-", 
            filterable: false 
        },
        { 
            key: "phone", 
            label: "Phone", 
            accessor: (row) => row?.phone || row?.phone_number || row?.mobile || row?.contact_phone || row?.contact?.phone || "-", 
            filterable: false 
        },
        { 
            key: "acquisition_source",
            label: "Source",
            accessor: (row) => getAcquisitionSourceLabel(row?.acquisition_source),
            filterable: true,
            width: "1fr"
        },
        {
            key: "primary_offer",
            label: "Primary Offer",
            accessor: (row) => getPrimaryOfferLabel(row?.primary_offer),
            filterable: true,
            width: "1fr"
        },
        {
            key: "value", 
            label: "Deal Value", 
            accessor: formatDealValue, 
            filterable: false 
        },
        { 
            key: "last_call", 
            label: "Last Call", 
            accessor: (row) => formatDashboardDateTime(row?.last_call ?? row?.lastCall ?? row?.last_call_at ?? row?.last_call_time), 
            filterable: true,
            quickValues: ["Most Recent", "Oldest"]
        },
        { 
            key: "outcome", 
            label: "Outcome", 
            accessor: (row) => getPhoneCallOutcomeLabel(row?.outcome ?? row?.last_outcome ?? row?.lastOutcome ?? row?.last_contact_outcome),
            filterable: false
        },
        { 
            key: "last_email", 
            label: "Last Email", 
            accessor: (row) => formatDashboardDateTime(row?.last_email ?? row?.lastEmail ?? row?.last_email_at ?? row?.last_email_time), 
            filterable: true,
            quickValues: ["Most Recent", "Oldest"]
        },
        { 
            key: "next_touch", 
            label: "Next Touch", 
            accessor: (row) => formatDashboardDateTime(row?.next_touch ?? row?.nextTouch ?? row?.next_touch_at ?? row?.next_step_at), 
            filterable: false
        },
        { 
            key: "touches", 
            label: "Touches", 
            accessor: (row) => formatDashboardTouches(row?.touches ?? row?.touch_count ?? row?.touchCount ?? row?.touches_count), 
            filterable: false 
        },
    ],
    actionsColumnWidth: "2fr",
};

function buildDealRowActions(row, onContactSelect, setLogCallContact) {
    const contact = buildDealContact(row);
    const hasContactRef = Boolean(contact.id || contact.name);

    return [
        {
            id: "view-contact",
            label: "View",
            icon: <FiEye aria-hidden="true" />,
            onClick: () => onContactSelect?.(contact),
            disabled: !hasContactRef,
        },
        {
            id: "log-call",
            label: "Call",
            icon: <FiPhone aria-hidden="true" />,
            onClick: () => setLogCallContact?.(contact),
            disabled: !hasContactRef,
        },
    ];
}

function Deals({ onContactSelect }) {
    const [deals, setDeals] = useState([]);
    const [loadingDeals, setLoadingDeals] = useState(false);
    const [selectedDeal, setSelectedDeal] = useState(null);
    const [logCallContact, setLogCallContact] = useState(null);

    const dealRowActions = useMemo(
        () => (row) => buildDealRowActions(row, onContactSelect, setLogCallContact),
        [onContactSelect]
    );

    async function loadDeals() {
        setLoadingDeals(true);
        try {
            const data = await getDeals();
            setDeals(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to load deals", err);
            setDeals([]);
        } finally {
            setLoadingDeals(false);
        }
    }

    useEffect(() => {
        loadDeals();
    }, []);

    return (
        <Page>
            <DataTable
                rowData={deals}
                loading={loadingDeals}
                tableConfig={DEALS_TABLE_CONFIG}
                onRowClick={(deal) => setSelectedDeal(deal)}
                rowActions={dealRowActions}
            />

            {selectedDeal ? (
                <DealDetailsModal
                    dealId={selectedDeal.id}
                    initialDeal={selectedDeal}
                    onClose={() => setSelectedDeal(null)}
                    onDealUpdated={(updatedDeal) => {
                        setDeals((prev) => prev.map((deal) =>
                            Number(deal.id) === Number(updatedDeal.id) ? { ...deal, ...updatedDeal } : deal
                        ));
                        setSelectedDeal((prev) => (prev && Number(prev.id) === Number(updatedDeal.id) ? { ...prev, ...updatedDeal } : prev));
                    }}
                    onDealDeleted={(deletedDealId) => {
                        setDeals((prev) => prev.filter((deal) => Number(deal.id) !== Number(deletedDealId)));
                        setSelectedDeal(null);
                    }}
                />
            ) : null}

            {logCallContact ? (
                <AddPhoneCall
                    isOpen={Boolean(logCallContact)}
                    onClose={() => setLogCallContact(null)}
                    deals={deals}
                    contacts={[logCallContact]}
                    onCreated={() => setLogCallContact(null)}
                />
            ) : null}
        </Page>
    );
}

export default Deals;